import React from 'react';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

/*
Usage (example):

import ImageSpinner from './ImageSpinner';

<ImageSpinner src="/logo.png" size={80} speed={1.2} pauseOnHover />

Props:
- src (string) required
- size (number|string) default 48
- speed (number seconds per rotation) default 1.5
- pauseOnHover (bool) default false
- direction ('normal'|'rever
*/

// Keyframes for rotation
const spin = (direction = 'normal') => keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(${direction === 'reverse' ? '-360deg' : '360deg'}); }
`;

const RotatingBox = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'speed' && prop !== 'direction' && prop !== 'pauseOnHover',
})(({ speed, direction, pauseOnHover }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${spin(direction)} ${
    typeof speed === 'number' ? speed : 1.5
  }s linear infinite`,
  // optionally pause on hover
  ...(pauseOnHover
    ? {
        '&:hover': {
          animationPlayState: 'paused',
        },
      }
    : {}),
}));

function ImageSpinner({
  src,
  size = 48,
  speed = 1.5,
  alt = '',
  pauseOnHover = false,
  direction = 'normal',
  style,
  imgProps,
  children,
}) {
  const numericSize =
    typeof size === 'number' ? size : parseInt(size, 10) || 48;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center">
      <RotatingBox
        speed={speed}
        direction={direction}
        pauseOnHover={pauseOnHover}
        sx={{
          width: numericSize,
          height: numericSize,
          overflow: 'hidden',
          borderRadius: '50%',
        }}
        style={style}>
        <img
          src={src}
          alt={alt}
          width={numericSize}
          height={numericSize}
          style={{ display: 'block', objectFit: 'cover' }}
          {...imgProps}
        />
      </RotatingBox>
      {children && <Box>{children}</Box>}
    </Box>
  );
}

export default ImageSpinner;
